import { useCurrentUser } from '@shared/hooks';
import { createContext, useContext, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

import { useInterruptedCashin } from './useInterruptedCashin';

type InterruptedCashinSession = ReturnType<typeof useInterruptedCashin>;

export const hasCashinPermission = (permissions: unknown): boolean =>
  Array.isArray(permissions) && permissions.some((permission) => permission?.name === 'cashin');

const InterruptedCashinContext = createContext<InterruptedCashinSession | null>(null);

export const InterruptedCashinProvider = ({ children }: { children: ReactNode }) => {
  const { pathname } = useLocation();
  const user = useCurrentUser();
  const value = useInterruptedCashin(pathname, hasCashinPermission(user.permissions));

  return <InterruptedCashinContext.Provider value={value}>{children}</InterruptedCashinContext.Provider>;
};

export const useInterruptedCashinSession = (): InterruptedCashinSession => {
  const value = useContext(InterruptedCashinContext);
  if (!value) {
    throw new Error('useInterruptedCashinSession must be used within InterruptedCashinProvider');
  }
  return value;
};

export const useOptionalInterruptedCashinSession = (): InterruptedCashinSession | null =>
  useContext(InterruptedCashinContext);

/** Stub de sesión para Storybook / demos sin el hook real. */
export const InterruptedCashinSessionStub = ({
  value,
  children,
}: {
  value: InterruptedCashinSession;
  children: ReactNode;
}) => <InterruptedCashinContext.Provider value={value}>{children}</InterruptedCashinContext.Provider>;
